import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'wicg-inert';
import Button from 'atoms/Button/Button';
import PortalBackdrop from 'atoms/PortalBackdrop/PortalBackdrop';
import { usePrevious } from 'hooks/usePrevious';
import CloseSvg from 'images/v5/icons/x-close.svg';
import { activateModal, closeModal, removeModal, selectModalByName } from 'store/layoutSlice';
import styles from './Modal.module.scss';
function Modal(props) {
    const { name, size = 'm', alwaysScroll = false, noPadding = false, header, subheader, options, footer, children, onClose, } = props;
    const dispatch = useDispatch();
    const modal = useSelector(selectModalByName(name));
    const { opened, active } = modal || {};
    const prevActive = usePrevious(active);
    const headerRef = useRef(null);
    const mainRef = useRef(null);
    const footerRef = useRef(null);
    const [scrollable, setScrollable] = useState(false);
    const [showFooterShadow, setShowFooterShadow] = useState(false);
    const contentClasses = classNames(styles.content, styles[size], {
        [styles.contentActive]: active,
        [styles.contentBottomPadding]: footer,
    });
    const mainClasses = classNames(styles.main, {
        [styles.scrollable]: scrollable,
        [styles.noPadding]: noPadding,
        [styles.withFooter]: footer,
    });
    const footerClasses = classNames(classNames(styles.footer, {
        [styles.footerShadow]: showFooterShadow,
    }));
    useEffect(() => {
        if (opened) {
            setTimeout(() => {
                dispatch(activateModal({ name }));
            }, 10);
        }
    }, [dispatch, opened, name]);
    useEffect(() => {
        if (prevActive && !active) {
            setTimeout(() => {
                dispatch(removeModal({ name }));
            }, 350);
        }
    }, [dispatch, active, prevActive, name]);
    useEffect(() => {
        if (!opened || !mainRef.current || !headerRef.current)
            return () => { };
        if (alwaysScroll) {
            setScrollable(true);
            return () => { };
        }
        const mainEl = mainRef.current;
        const headerEl = headerRef.current;
        const footerEl = footerRef.current;
        const contentEl = mainEl.firstElementChild;
        const updateHeights = () => {
            const totalAvailableHeight = window.innerHeight;
            const headerHeight = headerEl.offsetHeight;
            const footerHeight = (footerEl === null || footerEl === void 0 ? void 0 : footerEl.offsetHeight) || 0;
            const mainHeight = mainEl.scrollHeight;
            const mainVisibleHeight = mainEl.clientHeight;
            const maxAllowedHeight = totalAvailableHeight - headerHeight - footerHeight;
            setScrollable(mainHeight > maxAllowedHeight);
            setShowFooterShadow(mainHeight > mainVisibleHeight);
        };
        const resizeObserver = new ResizeObserver(() => {
            updateHeights();
        });
        if (contentEl)
            resizeObserver.observe(contentEl);
        resizeObserver.observe(mainEl);
        resizeObserver.observe(headerEl);
        if (footerEl)
            resizeObserver.observe(footerEl);
        requestAnimationFrame(updateHeights);
        window.addEventListener('resize', updateHeights);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateHeights);
        };
    }, [opened, alwaysScroll]);
    const handleModalClose = useCallback(() => {
        dispatch(closeModal({ name }));
        if (onClose) {
            onClose();
        }
    }, [dispatch, onClose, name]);
    return (React.createElement(PortalBackdrop, { opened: opened, active: active, onClose: handleModalClose },
        React.createElement("div", { className: contentClasses },
            header && (React.createElement("header", { className: styles.header, ref: headerRef },
                header,
                React.createElement("div", { className: styles.asideHeader },
                    options && options,
                    React.createElement(Button, { size: "s", theme: "ghost", shape: "rounded", leftIcon: React.createElement(CloseSvg, null), onClick: handleModalClose, testId: "modal-close" })))),
            React.createElement("main", { id: `modal-content-${name}`, className: mainClasses, ref: mainRef },
                subheader && React.createElement("div", { className: styles.subheader }, subheader),
                children),
            footer && (React.createElement("footer", { className: footerClasses, ref: footerRef }, footer)))));
}
export default Modal;
